import React, { useCallback, useContext, useMemo, useState } from 'react';

import DeleteIcon from '@mui/icons-material/Delete';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import { Alert, Badge, Button, Chip, IconButton } from '@mui/material';
import classNames from 'classnames';
import { differenceInCalendarMonths, format, getTime } from 'date-fns';
import { nanoid } from 'nanoid';
import numeral from 'numeral';
import { removeItem } from '../api/cart';
import { AccountContext } from '../context/account';
import { MapDataContext } from '../context/map-data';
import { CartBulkItem, CartItem } from '../core/global';
import { tokensModal, vatWarningMessage } from '../data/content';
import { ModelShapeItem } from '../data/layers';
import {
  getBasketCostForTokens,
  getCostFromTokens,
  itemExistsInArray
} from '../helpers/cart';
import styles from "../styles/components/cart-summary-table.module.scss";
import tableStyles from "../styles/components/table.module.scss";
import { CartPurchaseCostSummary, CartPurchaseTokensCostSummary } from './cart-purchase-cost-summary';
import { WarningMessage } from './error-message';
import { CreateSummary } from './form-components';
import { InfoModal } from './info-modal';
import { StyledMarkdown } from './typeface';

export const VatWarning = () => {
  return (
    <WarningMessage message={vatWarningMessage.content} />
  )
}

export interface TableProps {
  data: CartItem[]
}

export const CartSummaryTable = ({
  data
}: TableProps ) => {
  const [showTokenModal, setShowTokenModal] = useState(false)
  const [editedRowId, setEditedRowId] = useState<number | string | null>(null)
  const [deletingItem, setDeletingItem] = useState<number | string | null>(null)

  const {
    refetchCart,
    account,
    isVatExempt,
    paymentType,
    userActivePaymentMethod,
    userPaymentMethods,
    projects,
  } = useContext(AccountContext);

  const {
    dataSources,
  } = useContext(MapDataContext);
  
  const hasMultipleOrganisations = useMemo(() => {
    if (account) {
      const {organisation} = account
      return (organisation && Array.isArray(organisation) && organisation.length > 1)
    }
    return false
  }, [account])

  const getCost = useCallback((cost: number, tokens: number) => {
    if (paymentType === "card" || paymentType === "po") {
      return `£${numeral(cost).format("0,0.00")}`
    }
    if (paymentType === "tokens") {
      return `${numeral(tokens).format("0,0")} tokens`
    }
    const hasPayments = !(!userPaymentMethods || (Array.isArray(userPaymentMethods) && userPaymentMethods.length === 0))
    if (!hasPayments) {
      return `N/A`
    }
    return `Free`
  }, [paymentType, userPaymentMethods])


  const foundInProjects = useCallback(({start_date, end_date, lat, lon, model, version}: any) => {
    let foundInProjects: any[] = []

    if (start_date && end_date) {
      const item = {
        lat,
        lon,
        model,
        version,
        start_date: getTime(new Date(start_date)),
        end_date: getTime(new Date(end_date))
      }
      
      foundInProjects = itemExistsInArray({
        data: projects || [],
        ...item
      })
    }

    return foundInProjects
  }, [projects])


  return (
    <>
      <table className={tableStyles.table}>
        <thead>
          <tr>
            <th className={tableStyles.fieldName} style={{minWidth: '300px'}}>
              Model/Location
            </th>
            <th>Date range</th>
            {/* <th className={tableStyles.fieldName}>
              Project
            </th> */}
            <th className={tableStyles.currency}>
              {paymentType === "tokens" ?
                <Badge 
                  className={styles.badge}
                  badgeContent={"?"} 
                  color="info" 
                  onClick={()=> {
                    setShowTokenModal(true)
                  }}
                >
                Tokens
                </Badge>
              :
                <span>Cost</span>
              }
            </th>
            <th className={tableStyles.actions}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {data.map(item => {
              if (item) {
                const {
                  id,
                  lat,
                  lon,
                  model,
                  provider,
                  geography,
                  cost,
                  tokens,
                  start_date,
                  end_date,
                  project_id,
                  version,
                  params,
                } = item
                const key = `${provider}-${model}-${geography}-${version}`
                const isEdit = editedRowId !== null && editedRowId === id
                const range = `${format(new Date(start_date), 'MM/yyyy')} - ${format(new Date(end_date), 'MM/yyyy')}`
                const years = (start_date && end_date) ?
                  Math.abs(Math.ceil(differenceInCalendarMonths(new Date(end_date), new Date(start_date))/12))
                : null
                const currentDataSource: ModelShapeItem | null = (dataSources && Object.prototype.hasOwnProperty.call(dataSources, key)) ? dataSources[key] : null
                const config = currentDataSource?.options || null
                const isCompanyPurchase = !!(params !== null && userActivePaymentMethod?.config && userActivePaymentMethod?.config.company_id)
                const itemsFoundInProjects = foundInProjects({start_date, end_date, lat, lon, model, version})
                return (
                  <React.Fragment key={id}>

                    <tr key={id} className={classNames({
                      [tableStyles.deleting]: isEdit,
                      [tableStyles.noBorder]: (params !== null)
                    })}>
                      {/* <th>{lat}, {lon}</th> */}
                      <td>
                        <strong>{lat}, {lon}</strong><br />
                        {(currentDataSource) ? `${currentDataSource.name}` : ``}
                      </td>
                      <td className={tableStyles.date}>
                        {range}
                        {years && 
                          <Chip onClick={() => {}} style={{marginLeft: '16px'}} label={`${years} years`} variant="outlined" size="small" />
                        }
                      </td>
                      {/* <td className={tableStyles.date}><ProjectName projectId={project_id} /></td> */}
                      <td className={tableStyles.currency}>{getCost(cost, tokens)}</td>
                      {/* <td>{numeral(value).format("0,0.00")} {units}</td> */}
                      <td className={tableStyles.actions}>
                        {!isEdit ?
                          <IconButton 
                            size='small'
                            onClick={() => {
                              setEditedRowId(id)
                            }
                          }>
                            <DeleteIcon />
                          </IconButton>
                          :
                          <div>
                            <Button 
                              size='small'
                              color='error'
                              startIcon={<DeleteForeverIcon />}
                              onClick={async () => {
                                setDeletingItem(id)
                                await removeItem({
                                  id
                                })
                                refetchCart()
                                setDeletingItem(null)
                              }}>
                              Remove
                            </Button>
                            <Button 
                              size='small'
                              color="success"
                              onClick={() => {
                                setEditedRowId(null)
                              }}>
                              Keep
                            </Button>
                          </div>
                        }
                      </td>
                    </tr>
                    {params && 
                    <>
                      {params.created_at && <tr>
                        <td className={tableStyles.date} colSpan={4}>
                        <Alert severity="info">
                          <StyledMarkdown>
                            {`This is purchase request by **${params.first_name}${params.last_name? ` ${params.last_name}` : ``}**, requested on ${format(new Date(params.created_at), 'dd/MM/yyyy')}.  
${hasMultipleOrganisations ? `Purchase request is for: **${params.organisation_name}**` : `` }`}
                          </StyledMarkdown>
                        </Alert>
                        {isCompanyPurchase && userActivePaymentMethod?.config && userActivePaymentMethod?.config.company_id !== params?.organisation_id &&
                          <Alert severity="warning">
                            <StyledMarkdown>
                              {`Your selected payment processing company is **different** from the one from the request.`}
                            </StyledMarkdown>
                          </Alert>
                        }
                        </td>
                      </tr>
                      }
                      {(config && params && params.config) &&  <tr>
                        <td className={tableStyles.date} colSpan={4}>
                          <CreateSummary
                            className={classNames(tableStyles.summary, styles.infoTable)}
                            compact
                            values={params.config}
                            shape={config}
                          />
                        </td>
                      </tr>
                      }
                    </>
                      
                    }
                    {itemsFoundInProjects.length > 0 && 
                      <tr>
                        <td className={tableStyles.date} colSpan={4}>
                          <WarningMessage message={'You have previously acquired this data point for given data range and model.'} />
                        </td>
                      </tr>
                      }
                  </React.Fragment>
                )  
              }
              return null
            })
          }
        </tbody>
        <tfoot>
          <CartPurchaseCostSummary key={nanoid()} columnsPadding={1} />
        </tfoot>
      </table>
      {isVatExempt && (paymentType === "card" || paymentType === "po") && <VatWarning />}
      <InfoModal
        open={showTokenModal}
        title={tokensModal.title}
        content={tokensModal.content}
        maxWidth="sm"
        onClose={() => {
          setShowTokenModal(false)
        }}
      />
    </>
  )
}

export interface TokensSummaryTableProps {
  data: CartBulkItem[]
}

export const TokensSummaryTable = ({
  data
}: TokensSummaryTableProps ) => {
  const [showTokenModal, setShowTokenModal] = useState(false)
  const [editedRowId, setEditedRowId] = useState<number | string | null>(null)
  const [deletingItem, setDeletingItem] = useState<number | string | null>(null)

  const {
    refetchCart,
    account,
    isVatExempt,
    updatePurchaseTokens,
  } = useContext(AccountContext);



  const {
    vatValue, 
    vatPercentage, 
    subtotal,
    total,
  } = useMemo(() => {
    return getBasketCostForTokens( data.map((item: CartBulkItem) => {
      const {total} = getCostFromTokens(item)
      return total
    }), isVatExempt )
  }, [data, isVatExempt])

  return (
    <>
      <table className={tableStyles.table}>
        <thead>
          <tr>
            <th className={tableStyles.fieldName}>
              Item name
            </th>
            <th className={tableStyles.currency}>
              Purchased tokens
            </th>
            <th className={tableStyles.currency}>
              Discount
            </th>
            <th className={tableStyles.currency}>
              Cost
            </th>
            <th className={tableStyles.actions}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {data.map(item => {
              if (item) {
                const {
                  tokens,
                  name,
                  discount: discountPercent,
                  id
                } = item
                const {total, subtotal, discount} = getCostFromTokens(item)
                const isEdit = editedRowId !== null && editedRowId === id
                return (
                  <tr key={nanoid()} className={classNames({
                    [tableStyles.deleting]: isEdit
                  })}>
                    <td className={tableStyles.date}>{name}</td>
                    <td className={tableStyles.currency}>{numeral(tokens).format("0,0")}</td>
                    <td className={tableStyles.currency}>{numeral(discountPercent).format("0,0")}%</td>
                    <td className={tableStyles.currency}>£{numeral(total).format("0,0.00")}</td>
                    {/* <td>{numeral(value).format("0,0.00")} {units}</td> */}
                    <td className={tableStyles.actions}>
                      {!isEdit ?
                        <IconButton 
                          size='small'
                          onClick={() => {
                            setEditedRowId(id)
                          }
                        }>
                          <DeleteIcon />
                        </IconButton>
                        :
                        <div>
                          <Button 
                            size='small'
                            color='error'
                            startIcon={<DeleteForeverIcon />}
                            onClick={async () => {
                              updatePurchaseTokens( items => {
                                return items.filter( item => item.id !== id)
                              })
                            }}>
                            Remove
                          </Button>
                          <Button 
                            size='small'
                            color="success"
                            onClick={() => {
                              setEditedRowId(null)
                            }}>
                            Keep
                          </Button>
                        </div>
                      }
                    </td>
                  </tr>
                )  
              }
              return null
            })
          }
        </tbody>
        <tfoot>
          <CartPurchaseTokensCostSummary columnsPadding={2} />
        </tfoot>
      </table>
      {isVatExempt && <VatWarning />}
      <InfoModal
        open={showTokenModal}
        title={tokensModal.title}
        content={tokensModal.content}
        maxWidth="sm"
        onClose={() => {
          setShowTokenModal(false)
        }}
      />
    </>
  )
}

